import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { CurrentUser } from '@/javascript/interfaces'

import api from '../services/api'
import {
	deleteCertification,
	getCertificationsList,
	getCurriculums,
	getSharedCompletions,
	releaseCertification,
} from '../services/trainingCurriculums'
import type {
	Agency,
	Certification,
	Curriculum,
	PayerSource,
	ReleaseStatus,
	SharedCompletion,
	State,
} from '../services/trainingCurriculums/interfaces'
import { useCatalogStore } from './CatalogStore'
import GlobalStore from './GlobalStore'
import { useMemberTrainingStore } from './MemberTrainingStore'

export const useTrainingCurriculumStore = defineStore('trainingCurriculum', () => {
	// State
	const activeCertification = ref<{
		id: number | null
		name: string
		statusText: '' | 'disable' | 'release'
	}>({
		id: null,
		name: '',
		statusText: '',
	})
	const activeCurriculum = ref<Certification | Curriculum | undefined>(undefined)
	const agencies = ref<Agency[]>([])
	const certifications = ref<Certification[]>([])
	const currentUser = ref<CurrentUser | null>(null)
	const curriculumSearchInput = ref('')
	const curriculums = ref<Curriculum[]>([])
	const filterPayerSourceTo = ref<PayerSource>((sessionStorage.getItem('filterPayerSourceTo') as PayerSource) || 'both')
	const releasedStatus = ref<ReleaseStatus>('')
	const requestInProgress = ref(true)
	const selectedCertifications = ref<Certification[]>([])
	const selectedCurriculums = ref<Curriculum[]>([])
	const sharedCompletionCourses = ref<SharedCompletion[]>([])
	const states = ref<State[]>([])
	const successNotification = ref('')
	const trainingPackageCourseList = ref<string[]>([])

	// Getters
	const curriculumsPrivatePayCount = computed(() => {
		return curriculums.value.filter(c => c.payerSource === 'private').length
	})

	const curriculumsPublicPayCount = computed(() => {
		return curriculums.value.filter(c => c.payerSource === 'public').length
	})

	const filteredCertifications = computed((): Certification[] => {
		let filtered = certifications.value

		// filter based on search input
		if (curriculumSearchInput.value.length) {
			filtered = filtered.filter(curriculum => {
				const searchTerms = curriculumSearchInput.value.trim().toLowerCase().split(/ +/)

				return curriculum.searchableString ?
					searchTerms.every(term => curriculum.searchableString.toLowerCase().indexOf(term) > -1) :
					false
			})
		}

		switch (releasedStatus.value) {
			case 'disabled':
				return filtered.filter(c => !c.released_at)

			case 'released':
				return filtered.filter(c => !!c.released_at)
		}

		return filtered
	})

	const filteredCurriculums = computed((): Curriculum[] => {
		let filtered = curriculums.value

		if ([
			'private',
			'public',
		].includes(filterPayerSourceTo.value)) {
			filtered = filtered.filter(
				c => c.payerSource === filterPayerSourceTo.value
			)
		}

		if (curriculumSearchInput.value.length) {
			filtered = filtered.filter(curriculum => {
				const searchTerms = curriculumSearchInput.value.trim().toLowerCase().split(/ +/)

				return curriculum.searchableString ?
					searchTerms.every(term => curriculum.searchableString.toLowerCase().indexOf(term) > -1) :
					false
			})
		}

		return filtered
	})

	const payerSourceCurriculums = computed((): Curriculum[] => {
		return curriculums.value.filter(c =>
			filterPayerSourceTo.value === 'both' ?
				true : c.payerSource === filterPayerSourceTo.value
		)
	})

	const selectedCurriculumIds = computed((): number[] => {
		return selectedCurriculums.value.map(c => c.id)
	})

	const hasSelectedCurriculums = computed(() => {
		return selectedCertifications.value.length > 0 || selectedCurriculums.value.length > 0
	})

	const isCertifications = computed(() => {
		return filterPayerSourceTo.value === 'certifications'
	})

	// Actions
	const disableOrReleaseCertifications = async () => {
		const id = activeCertification.value.id!
		const name = activeCertification.value.name
		const statusText = activeCertification.value.statusText

		requestInProgress.value = true

		try {
			if (statusText === 'release') {
				await releaseCertification(api, { curriculumIds: [id] })
			} else {
				await deleteCertification(api, { id: id })
			}

			await loadCurriculums()

			GlobalStore.onHandleSuccessMessages({ message: `${name} has been successfully ${statusText}d.` })

			activeCertification.value = {
				id: null,
				name: '',
				statusText: '',
			}
			activeCurriculum.value = undefined
			GlobalStore.resetCAModal()
		} catch (error: unknown) {
			const err = error as { response?: { data?: unknown } }

			GlobalStore.onHandleErrorMessages({ error: err.response?.data })
		}
	}

	const fetchSharedCompletions = async (memberId: number) => {
		try {
			const response = await getSharedCompletions(api, memberId)

			sharedCompletionCourses.value = response.shared_completions.map((course: SharedCompletion) => ({
				...course,
				courseStatusActions: {
					renaming: false,
					selected: false,
					type: 'courses',
				},
			}))

			const memberTrainingStore = useMemberTrainingStore()

			memberTrainingStore.selectedSharedCompletionIds = []
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		}
	}

	const loadCurriculums = async () => {
		try {
			const {
				agencies: agenciesData,
				curriculums: curriculumsData,
				states: statesData,
			} = await getCurriculums(api)
			const { curriculums: certificationsData } = await getCertificationsList(api)

			agencies.value = agenciesData
			states.value = statesData.map((s: string[]) => ({
				abbreviation: s[1], name: s[0], timezone: s[2],
			}))

			certifications.value = certificationsData.map((o: Certification) => ({
				...o,
				payerSource: 'certifications',
				searchableString: o.name,
			}))

			curriculums.value = curriculumsData
			useCatalogStore().itemType = 'specialized'
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		} finally {
			requestInProgress.value = false
		}
	}

	const onSearch = (stringToSearch: string, searchFor: string): string[] | boolean => {
		const searchTerms = searchFor.trim().toLowerCase().split(/ +/)

		return !!stringToSearch ? searchTerms.every(term => stringToSearch.toLowerCase().indexOf(term) > -1) : searchTerms
	}

	const resetSelectedCurriculums = () => {
		selectedCertifications.value = []
		selectedCurriculums.value = []
		GlobalStore.resetCAModal()
	}

	const setPayerSourceFilterTo = (payerSource: PayerSource) => {
		resetSelectedCurriculums()
		filterPayerSourceTo.value = payerSource || 'both'
		sessionStorage.setItem('filterPayerSourceTo', payerSource)
	}

	const updateSelectAllCertifications = () => {
		const hasSelectedCertifications = filteredCertifications.value.length > 0 && selectedCertifications.value.length > 0 &&
			filteredCertifications.value.length === selectedCertifications.value.length

		if (hasSelectedCertifications) {
			selectedCertifications.value = []
		} else {
			selectedCertifications.value = [...filteredCertifications.value]
		}
	}

	const updateSelectAllCurriculums = () => {
		const hasSelectedCurriculums = filteredCurriculums.value.length > 0 && selectedCurriculums.value.length > 0 &&
			filteredCurriculums.value.length === selectedCurriculums.value.length

		if (hasSelectedCurriculums) {
			selectedCurriculums.value = []
		} else {
			selectedCurriculums.value = [...filteredCurriculums.value]
		}
	}

	const updateSelectedCertifications = (value: Certification) => {
		const selectedIndex = selectedCertifications.value.findIndex(c => c.id === value.id)

		if (selectedIndex >= 0) {
			selectedCertifications.value.splice(selectedIndex, 1)
		} else {
			selectedCertifications.value.push(value)
		}
	}

	const updateSelectedCurriculums = (value: Curriculum) => {
		const selectedIndex = selectedCurriculums.value.findIndex(c => c.id === value.id)

		if (selectedIndex >= 0) {
			selectedCurriculums.value.splice(selectedIndex, 1)
		} else {
			selectedCurriculums.value.push(value)
		}
	}

	return {
		// State
		activeCertification,
		activeCurriculum,
		agencies,
		certifications,
		currentUser,
		curriculumSearchInput,
		curriculums,
		filterPayerSourceTo,
		releasedStatus,
		requestInProgress,
		selectedCertifications,
		selectedCurriculums,
		sharedCompletionCourses,
		states,
		successNotification,
		trainingPackageCourseList,

		// Getters
		curriculumsPrivatePayCount,
		curriculumsPublicPayCount,
		filteredCertifications,
		filteredCurriculums,
		payerSourceCurriculums,
		selectedCurriculumIds,
		hasSelectedCurriculums,
		isCertifications,

		// Actions
		disableOrReleaseCertifications,
		fetchSharedCompletions,
		loadCurriculums,
		onSearch,
		resetSelectedCurriculums,
		setPayerSourceFilterTo,
		updateSelectAllCertifications,
		updateSelectAllCurriculums,
		updateSelectedCertifications,
		updateSelectedCurriculums,
	}
})
